import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import stringify from 'fast-json-stable-stringify';
import SubscriptionCallToActionComponent
  from '@fuww/library/src/SubscriptionCallToAction';
import messages from '../../lib/messages.json';
import LinkAndAnchor from '../LinkAndAnchor';
import { saveRouterState } from '../../lib/routerState';
import createAnalyticsEvent
  from '../../lib/mutations/createAnalyticsEvent.graphql';
import { AnalyticsEventType } from '../../lib/graphql/api/graphql';
import { useSiteContext } from '../SiteContext';
import getArticleSubjectType from '../../lib/getArticleSubjectType';
import { UserContext } from '../UserProvider';

const annualBillingPrice = '€180';
const monthlyBillingPrice = '€18';
const annualBillingMonthlyAmount = '€15';
const annualBillingAnnualSavings = '€36';

const SubscriptionCallToAction = () => {
  const intl = useIntl();
  const router = useRouter();
  const [createPageview] = useMutation(createAnalyticsEvent);
  const { route, query } = useRouter();
  const { query: { id } } = useRouter();
  const subjectId = id ? `${id}` : null;

  const stringifiedRouter = stringify(router);
  useEffect(() => {
    saveRouterState(JSON.parse(stringifiedRouter));
  }, [stringifiedRouter]);

  const {
    prefix,
    hasLocalNews,
    url,
    newsboard,
  } = useSiteContext();

  const { user } = useContext(UserContext);

  // eslint-disable-next-line no-underscore-dangle
  const userId = user?._id;

  const canonicalRouteQuery = stringify({ route, query });

  useEffect(() => {
    createPageview({
      variables: {
        type: AnalyticsEventType.EVENT,
        url,
        title: 'subscription callToAction',
        subjectType: id ? getArticleSubjectType(prefix, hasLocalNews) : null,
        subjectId: subjectId || null,
        payload: JSON.stringify({
          newsboard,
          userId,
        }),
      },
    });
    // don't re-run it even when data changes
    // only route and query changes should trigger this hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalRouteQuery]);

  return (
    <SubscriptionCallToActionComponent
      callToActionMessage={intl.formatMessage(
        messages['subscriptionCallToAction.callToAction'],
      )}
      sellingPoints={[
        intl.formatMessage(
          messages['subscriptionCallToAction.exclusiveAccess'],
        ),
        intl.formatMessage(
          messages['subscriptionCallToAction.insiderForecasts'],
        ),
        intl.formatMessage(
          messages['subscriptionCallToAction.directInsights'],
        ),
      ]}
      youCanCancelMessage={intl.formatMessage(
        messages['subscriptionCallToAction.youCanCancel'],
      )}
      startReadingMessage={intl.formatMessage(
        messages['subscriptionCallToAction.startReading'],
      )}
      complimentaryAccessMessage={intl.formatMessage(
        messages['subscriptionCallToAction.complimentaryAccess'],
      )}
      subscriptionPriceMessage={intl.formatMessage(
        messages['subscriptionCallToAction.subscriptionPrice'],
      )}
      yearlyMessage={intl.formatMessage(
        messages['subscriptionFlow.asideContent.yearly'],
      )}
      monthlyMessage={intl.formatMessage(
        messages['subscriptionFlow.asideContent.monthly'],
      )}
      savePerYearMessage={intl.formatMessage(
        messages['subscriptionCallToAction.savePerYear'],
        { amount: annualBillingAnnualSavings },
      )}
      perMonthAbbreviationMessage={intl.formatMessage(
        messages['subscriptionCallToAction.perMonthAbbreviation'],
      )}
      billedAnnuallyMessage={intl.formatMessage(
        messages['subscriptionCallToAction.billedAnnually'],
        { amount: annualBillingPrice },
      )}
      bestValueMessage={intl.formatMessage(
        messages['subscriptionCallToAction.bestValue'],
      )}
      billedMonthlyMessage={intl.formatMessage(
        messages['subscriptionCallToAction.billedMonthly'],
      )}
      annualBillingMonthlyAmount={annualBillingMonthlyAmount}
      monthlyBillingPrice={monthlyBillingPrice}
      LinkComponent={LinkAndAnchor}
      onClick={() => {
        saveRouterState(router);
      }}
    />
  );
};

export default SubscriptionCallToAction;
